import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useEffect } from 'react';
import { Typography, RichTextViewer, TextButton } from 'seagull';
import { Checkmark } from 'seagull/icons';
import { getCoverCCFeesChangeBullet, getCoverCCFeesText, getNetTermsChangeBullet } from '~/business-logic/amendmentsLogic';
import { useViewLegalTerms } from '~/components/LegalTermsDropdown/hooks/useViewLegalTerms';
import { useStore, useStoreActions } from '~/contexts';
import { PracticeManagementSyncFailures } from '~/integrations/PracticeManagement/PracticeManagementSyncFailures/PracticeManagementSyncFailures';
import { legalTermsModel, relationshipModel } from '~/models';
import { AgreementDataModel } from '~/models/AgreementDataModel';
import { selectors } from '~/store/state-selectors';
import styles from './AgreementView.module.scss';
import AgreementAmendmentsApprovalMethod from './components/AgreementAmendmentsApprovalMethod';
import AgreementInvoicesApprovalMethod from './components/AgreementInvoicesApprovalMethod';
import AmendmentChangesInAgreementView from './components/AmendmentChangesInAgreementView';
import ChangesSummary from './components/ChangesSummary';
import { AgreementViewCard, SpecificTerm } from './components/CommonComponents';
import Header from './components/Header';
import SignedHeader from './components/SignedHeader';
import StatementOfWork from './components/StatementOfWork';
const autoprefix = 'agreement-view-';
export default function AgreementView(props) {
    var _relationship_practiceManagementSyncFailures;
    const { relationship, isVendor, refresh } = props;
    const { extraClause } = relationship;
    const { buttonPrimary } = useStore(selectors.user.branding);
    const qboClasses = useStore((state)=>state.qboClasses.data);
    const { fetchQboClasses } = useStoreActions();
    const { viewLegalTerms } = useViewLegalTerms({
        vendorBusinessId: relationship.vendor.id
    });
    const updatedNetTerms = relationshipModel.updatedNetTerms(relationship);
    const netTermsChangeBullet = updatedNetTerms ? getNetTermsChangeBullet(relationship, updatedNetTerms.value()) : null;
    const updatedIsVendorCoveringCCFee = relationshipModel.updatedIsVendorCoveringCCFee(relationship);
    const agreementData = AgreementDataModel.fromRelationship(relationship);
    const legalTerms = useMemo(()=>legalTermsModel.fromRelationshipLegalTerms(relationship.legalTermsDocuments), [
        relationship.legalTermsDocuments
    ]);
    useEffect(()=>{
        fetchQboClasses();
    }, []);
    const coverCCFeesChangeBullet = updatedIsVendorCoveringCCFee ? getCoverCCFeesChangeBullet(relationship, updatedIsVendorCoveringCCFee.value()) : null;
    const effectiveDateTerm = relationship.effectiveDate && /*#__PURE__*/ _jsx(SpecificTerm, {
        field: "Effective date of agreement",
        value: relationshipModel.formatEffectiveDate(relationship),
        testId: autoprefix + 'effective-date'
    });
    const termsOfServiceList = useMemo(()=>{
        if (!legalTerms || legalTerms.length === 0) return null;
        const getName = (doc)=>{
            var _doc_name;
            return doc === null || doc === void 0 ? void 0 : (_doc_name = doc.name) === null || _doc_name === void 0 ? void 0 : _doc_name.replace(/\.pdf$/i, '');
        };
        const renderButton = (doc, text)=>{
            var _ref;
            return /*#__PURE__*/ _jsx(TextButton, {
                testId: "tos-".concat(doc === null || doc === void 0 ? void 0 : doc.id),
                bi: {
                    name: 'view_terms_of_service',
                    data: {
                        document_id: (_ref = doc === null || doc === void 0 ? void 0 : doc.id) !== null && _ref !== void 0 ? _ref : ''
                    }
                },
                onClick: ()=>viewLegalTerms(doc, relationship.id),
                children: text
            });
        };
        const value = legalTerms.length === 1 ? renderButton(legalTerms[0], 'View') : legalTerms.map((doc, index, arr)=>/*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    renderButton(doc, getName(doc)),
                    index < arr.length - 1 && ', '
                ]
            }));
        return /*#__PURE__*/ _jsx(SpecificTerm, {
            testId: autoprefix + 'tos-list',
            field: "Terms of Service",
            value: value
        });
    }, [
        legalTerms,
        relationship.id,
        viewLegalTerms
    ]);
    const termsAcceptedAtSigning = useMemo(()=>{
        var _relationship_legalTermsDocuments;
        const statements = (_relationship_legalTermsDocuments = relationship.legalTermsDocuments) === null || _relationship_legalTermsDocuments === void 0 ? void 0 : _relationship_legalTermsDocuments.reduce((acc, term)=>{
            var _term_termsOfService_explicitApproval;
            if (term.termsOfService.type === 'explicitApproval' && ((_term_termsOfService_explicitApproval = term.termsOfService.explicitApproval) === null || _term_termsOfService_explicitApproval === void 0 ? void 0 : _term_termsOfService_explicitApproval.statement)) {
                var _term_termsOfService_explicitApproval1;
                acc.push((_term_termsOfService_explicitApproval1 = term.termsOfService.explicitApproval) === null || _term_termsOfService_explicitApproval1 === void 0 ? void 0 : _term_termsOfService_explicitApproval1.statement);
            }
            return acc;
        }, []);
        if (!statements || statements.length === 0) return null;
        return /*#__PURE__*/ _jsx(SpecificTerm, {
            testId: autoprefix + 'terms-accepted-at-signing',
            field: "Terms explicitly accepted at signing",
            value: statements === null || statements === void 0 ? void 0 : statements.map((statement, index)=>/*#__PURE__*/ _jsxs("div", {
                    className: styles.termsAcceptanceContainer,
                    children: [
                        /*#__PURE__*/ _jsx(Checkmark, {}),
                        /*#__PURE__*/ _jsx(Typography, {
                            className: styles.termsAcceptanceStatement,
                            weight: "light",
                            children: statement
                        }, "termsAcceptence-".concat(index, "-statement"))
                    ]
                }, "termsAcceptance-".concat(index, "-container")))
        });
    }, [
        relationship.legalTermsDocuments
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Header, {
                relationship: relationship,
                onUpdate: refresh
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.main,
                "data-testid": "agreement-view",
                children: [
                    /*#__PURE__*/ _jsx(ChangesSummary, {
                        isVendor: isVendor,
                        relationship: relationship,
                        testId: autoprefix + 'changes',
                        refresh: refresh
                    }),
                    ((_relationship_practiceManagementSyncFailures = relationship.practiceManagementSyncFailures) === null || _relationship_practiceManagementSyncFailures === void 0 ? void 0 : _relationship_practiceManagementSyncFailures.length) && /*#__PURE__*/ _jsx(PracticeManagementSyncFailures, {
                        relationship: relationship,
                        refresh: refresh
                    }),
                    /*#__PURE__*/ _jsx(SignedHeader, {
                        relationship: relationship,
                        brandColor: buttonPrimary,
                        testPrefix: autoprefix
                    }),
                    /*#__PURE__*/ _jsx(AgreementViewCard, {
                        headTitle: "Services Provided",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: styles.services,
                            children: /*#__PURE__*/ _jsx(StatementOfWork, {
                                services: relationship.services,
                                isVendor: isVendor,
                                qboClasses: qboClasses,
                                serviceDetailsProps: {
                                    agreementData,
                                    isVendor,
                                    relationshipState: {
                                        type: 'approved',
                                        effectiveDate: relationshipModel.effectiveDate(relationship)
                                    }
                                }
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(AgreementViewCard, {
                        headTitle: "Agreement terms",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: styles.specificTerms,
                            children: [
                                termsOfServiceList,
                                termsAcceptedAtSigning,
                                effectiveDateTerm,
                                /*#__PURE__*/ _jsx(SpecificTerm, {
                                    field: "Payment terms",
                                    value: relationshipModel.paymentTermsText(relationship),
                                    testId: autoprefix + 'net-terms',
                                    changes: netTermsChangeBullet && /*#__PURE__*/ _jsx(AmendmentChangesInAgreementView, {
                                        disableBorderRadius: true,
                                        items: [
                                            netTermsChangeBullet
                                        ],
                                        autoApprovalDate: relationshipModel.updatedNetTermsAutoApprovalDate(relationship),
                                        testId: autoprefix + 'net-terms-changes'
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecificTerm, {
                                    testId: autoprefix + 'cc-fees',
                                    field: "Card fees",
                                    value: getCoverCCFeesText(relationshipModel.actualIsVendorCoveringCCFee(relationship), relationshipModel.vendorName(relationship), relationshipModel.customerName(relationship)),
                                    changes: coverCCFeesChangeBullet && /*#__PURE__*/ _jsx(AmendmentChangesInAgreementView, {
                                        disableBorderRadius: true,
                                        items: [
                                            coverCCFeesChangeBullet
                                        ],
                                        autoApprovalDate: relationshipModel.updatedIsVendorCoveringCCFeeAutoApprovalDate(relationship),
                                        testId: autoprefix + 'cc-fees-changes'
                                    })
                                }),
                                /*#__PURE__*/ _jsx(AgreementAmendmentsApprovalMethod, {
                                    relationship: relationship,
                                    testId: autoprefix
                                }),
                                /*#__PURE__*/ _jsx(AgreementInvoicesApprovalMethod, {
                                    relationship: relationship,
                                    testId: autoprefix
                                }),
                                extraClause && /*#__PURE__*/ _jsx(SpecificTerm, {
                                    field: "Notes",
                                    value: /*#__PURE__*/ _jsx(RichTextViewer, {
                                        testId: 'extra-clause-rich-text-viewer',
                                        value: extraClause,
                                        skin: "secondary"
                                    })
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
}
